import PropTypes from 'prop-types';

import { PatientGptBanner as PatientGptBannerMolecule } from '@/design-system/molecules/PatientGptBanner';
import { usePatientGptSubmit } from '@/hooks/usePatientGptSubmit';

/**
 * PatientGptBanner feature module.
 *
 * Composes the PatientGPT Banner molecule with the shared `usePatientGptSubmit` hook, which owns
 * the validation and full submit lifecycle (shared with the PatientGPT Hero):
 *   - validate (required, 2–150 chars)
 *   - call the session client
 *   - hold pending / error state
 *   - guard double-submit
 *   - navigate to the returned tokenised URL
 *
 * Composes the presentational molecule (@/design-system/molecules/PatientGptBanner)
 * and forwards the nine control props it needs (spec §6). Does NOT register a
 * MapTo resourceType — that is owned by the separate Container Adapter child spec.
 */
const PatientGptBanner = ({
    title,
    description,
    ctaLabel,
    ctaAriaLabel,
    ctaIconName,
    inputLabel,
    theme,
    apiBaseUrl,
    validationErrorMessage,
    apiErrorMessage,
    isHeroEmbedded,
}) => {
    // Validation, submit lifecycle, analytics and navigation are shared with the PatientGPT Hero.
    const {
        value,
        isInvalid,
        isPending,
        errorMessage,
        maxLength,
        handleChange,
        handleBlur,
        handleSubmit,
    } = usePatientGptSubmit({
        title,
        ctaLabel,
        apiBaseUrl,
        validationErrorMessage,
        apiErrorMessage,
    });

    // ── Render (spec §6) ───────────────────────────────────────────────────────
    // Forward an enumerated prop list — never a spread — so SPA-Editor
    // housekeeping props (cqPath, isInEditor, appliedCssClassNames, …) that
    // arrive from the adapter stop here and never reach the design-system
    // molecule (spec §6, "Forwarding style").
    return (
        <PatientGptBannerMolecule
            title={title}
            description={description}
            ctaLabel={ctaLabel}
            ctaAriaLabel={ctaAriaLabel}
            ctaIconName={ctaIconName}
            inputLabel={inputLabel}
            maxLength={maxLength}
            theme={theme}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            onSubmit={handleSubmit}
            isPending={isPending}
            isInvalid={isInvalid}
            errorMessage={errorMessage}
            isHeroEmbedded={isHeroEmbedded}
        />
    );
};

// ── PropTypes (spec §6, proptypes-required) ────────────────────────────────────
// Declared here — not on the MapTo adapter (which is exempt) and not on the
// molecule alone.  Every prop accepted is declared; required props are marked
// .isRequired.
//
// theme and ctaIconName are declared PropTypes.string.isRequired rather than
// the molecule's domain enums (Theme.propTypes.name / Icon.propTypes.name)
// because this module reads no meaning from either value and forwards them
// untouched.  The adapter already enforces the three-token allowlist for theme
// (adapter A-09); the molecule validates domains against the atom enums it owns.
// One layer per concern (spec §6).
PatientGptBanner.propTypes = {
    /** H2 title forwarded to the molecule. */
    title: PropTypes.string.isRequired,
    /** Optional rich-text description forwarded to the molecule. */
    description: PropTypes.string,
    /** Visible CTA button label forwarded to the molecule. */
    ctaLabel: PropTypes.string.isRequired,
    /** Accessible-name override for the CTA; molecule falls back to ctaLabel when empty. */
    ctaAriaLabel: PropTypes.string,
    /** Icon name forwarded to the molecule's CTA button. */
    ctaIconName: PropTypes.string.isRequired,
    /** Input's accessible name forwarded to the molecule. */
    inputLabel: PropTypes.string.isRequired,
    /** Resolved theme surface, forwarded untouched. Default already applied by the adapter. */
    theme: PropTypes.string.isRequired,
    /** API origin, passed to createSession; never forwarded to the molecule. */
    apiBaseUrl: PropTypes.string.isRequired,
    /** Authored copy shown when validation fails; never rendered by the molecule directly. */
    validationErrorMessage: PropTypes.string.isRequired,
    /** Authored copy shown when the session request fails; never rendered by the molecule directly. */
    apiErrorMessage: PropTypes.string.isRequired,
    isHeroEmbedded: PropTypes.bool,
};

PatientGptBanner.defaultProps = {
    description: '',
    // Default '' (not undefined) — the molecule's '' → ctaLabel fallback is keyed on
    // empty string (molecule §7, parent Q-07), and the adapter already forwards ''
    // rather than coercing it (adapter A-06).
    ctaAriaLabel: '',
    isHeroEmbedded: false,
};

export default PatientGptBanner;
